import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Modal, Steps, Tooltip, message, notification } from 'antd';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { memo, useEffect, useMemo, useState } from 'react';
import DescCards from '../DescCards/index';
import ContentEnrichmentModal from './components/ContentEnrichmentModal';
import ReferenceDocumentModal from './components/ReferenceDocumentModal';
import { Step1Progress } from './components/Step1Progress';
import { DOC_TYPES, DocType, STEPS } from './const';

// 文档类型切换项（原首页样式）；mobileHidden：手机上不显示（桌面不变）
const DocTypeItem = ({ doc, isActive, onClick, isSpecial, mobileHidden }) => (
    <div
        role="button"
        tabIndex={0}
        aria-pressed={isActive}
        className={classNames(styles.docTypeItem, {
            [styles.active]: isActive,
            [styles.special]: isSpecial,
            [styles.mobileHidden]: mobileHidden,
        })}
        data-track="type_selected"
        data-track-label={doc.key}
        onClick={onClick}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
            }
        }}
    >
        {!isSpecial && <img src={doc.icon} alt="" />}
        <span>{doc.label}</span>
        {isSpecial && <img src={doc.icon} alt="" />}
    </div>
);
import { Footer } from './Footer';
import styles from './index.module.scss';
import Step1 from './Step1/index';
import Step2 from './Step2/index';
import { summaryStore } from './store';
import { Progress } from './Summary/Progress';
import { UltraDialog } from './UltraDialog';

import iconCodeActive from '../../assets/icon-code-active.svg';
import iconDiagramActive from '../../assets/icon-diagram-active.svg';
import iconFormulaActive from '../../assets/icon-formula-active.svg';
import iconTableActive from '../../assets/icon-table-active.svg';
import titleExtra from '../../assets/title-extra.png';

import { debounce } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import AiGenTip from '../../components/aiGenTip';
import { useDevice } from '../../hooks/useDevice';
import { MODEL } from '../../pages/writing/api';
import CaseLibrary from '../../pages/writing/CaseLibrary';
import EngineSwitch from '../../pages/writing/EngineSwitch';
import RecentOrders from '../../pages/writing/RecentOrders';
import StepForm from '../../pages/writing/StepForm';
import { Step1 as ReduceStep1 } from '../Reduce/Step1';
import { reduceStore } from '../Reduce/store';
import {
    DOC_TYPE_TO_QUERY_TYPE,
    GRADUATION_THESIS_STEPS,
    MOBILE_HIDDEN_DOC_TYPES,
    NEW_WRITING_DOC_TYPES,
    PPT_STEPS,
    QUERY_TYPE_TO_DOC_TYPE,
    REDUCE_STEPS,
} from './const';
import GraduationThesisStep1 from './GraduationThesisSteps/GraduationThesisStep1';
import GraduationThesisStep2 from './GraduationThesisSteps/GraduationThesisStep2';
import GraduationThesisStep3 from './GraduationThesisSteps/GraduationThesisStep3';
import PPTStep1 from './PPTStep1/index';
import UltraSwitch from './Step1/components/UltraSwitch/UltraSwitch';
import Step3 from './Step3/index';
import { CodeIcon, DataIcon, GongshiIcon, PicIcon, TableIcon } from './Summary/icons';
// 添加Background组件
export const Background = ({ useUltra }) => (
    <div
        className={classNames(styles.bg, {
            [styles.bgUltra]: useUltra,
        })}
    ></div>
);

export const Documents = observer(() => {
    const [notiApi, notiContextHolder] = notification.useNotification();
    const [modal, contextHolder] = Modal.useModal();

    const { isMobile } = useDevice();
    const navigate = useNavigate();

    const [qs] = useSearchParams();
    useEffect(() => {
        if (
            qs.get('package') === 'selected' &&
            Object.values(DocType).includes(qs.get('docType'))
        ) {
            // 答辩PPT 已迁到独立的 PPT 工作室
            if (qs.get('docType') === DocType.DEFENSE_PPT) {
                navigate('/ppt', { replace: true });
                return;
            }
            summaryStore.setDocType(qs.get('docType'));
        }
    }, [qs, navigate]);
    // 获取并保存inviter参数
    useEffect(() => {
        const inviter = qs.get('inviter');
        if (inviter) {
            window.localStorage.setItem('inviter', inviter);
        }
    }, [qs]);

    const isPPT = summaryStore.docType === DocType.DEFENSE_PPT;
    const isReduce = summaryStore.docType === DocType.THESIS_MODIFICATION;
    const isGraduationThesis =
        summaryStore.docType === DocType.GRADUATION_THESIS ||
        summaryStore.docType === DocType.TERM_PAPER ||
        summaryStore.docType === DocType.PROPOSAL_REPORT ||
        summaryStore.docType === DocType.LITERATURE_REVIEW;
    const items = useMemo(() => {
        if (isPPT) {
            return PPT_STEPS.map((item) => ({
                key: item.key,
                title: item.title,
                disabled: true,
            }));
        }
        if (isGraduationThesis) {
            return GRADUATION_THESIS_STEPS.map((item) => ({
                key: item.key,
                title: item.title,
                disabled: true,
            }));
        }
        if (isReduce) {
            return REDUCE_STEPS.map((item) => ({
                key: item.key,
                title: item.title,
                disabled: true,
            }));
        }
        return STEPS.map((item) => ({
            key: item.key,
            title: item.title,
            disabled: true,
        }));
    }, [isGraduationThesis, isPPT, isReduce]);

    const [top, setTop] = useState(0);

    const onScroll = useMemo(
        () =>
            debounce(() => {
                const el = document.getElementById('summaryEl');
                if (!el) return;
                const maxH = el ? el.offsetHeight - 180 : 1000;
                const distance = document.scrollingElement.scrollTop - el.offsetTop + 196;
                setTop(Math.min(Math.max(0, distance), maxH));
            }, 500),
        [],
    );

    useEffect(() => {
        document.addEventListener('scroll', onScroll);
        return () => {
            document.removeEventListener('scroll', onScroll);
        };
    }, []);

    const curActiveDocItem = useMemo(() => {
        return DOC_TYPES.find((item) => item.key === summaryStore.docType);
    }, [summaryStore.docType]);

    // ---------- 新版写作：毕业长文 / 开题报告 / 文献综述 / 课程长文 ----------
    const isNewWriting = NEW_WRITING_DOC_TYPES.includes(summaryStore.docType);
    // 写作流程当前步骤：第一步需求表单直接放在首页，参考资料 / 确认大纲 / 预览全文铺满宽度
    const [writingStep, setWritingStep] = useState(1);
    const onWritingHome = writingStep === 1;
    // 生成引擎（需求配置 / 大纲页共用）
    const [writingModel, setWritingModel] = useState(MODEL.NORMAL);

    // 从其它功能切回来时表单重新从第一步开始
    useEffect(() => {
        if (!isNewWriting) setWritingStep(1);
    }, [isNewWriting]);

    // 新版写作不使用 Normal/Ultra 切换：带着 Ultra 进入时改为默认选中「超级自研学术特调模型」
    useEffect(() => {
        if (isNewWriting && summaryStore.useUltra) {
            setWritingModel(MODEL.ACADEMIC);
            summaryStore.initApiVersion();
        }
    }, [isNewWriting, summaryStore.useUltra]);

    const selectDocType = (docKey) => {
        summaryStore.reset();
        summaryStore.autoLightTypes = [];
        summaryStore.setDocType(docKey);
    };

    const handleDocTypeClick = (doc) => {
        // 如果在格式精修下，直接跳转
        if (doc.key === DocType.FORMATTER) {
            navigate('/formatter');
            return;
        }
        // 答辩PPT 进入独立的 PPT 工作室
        if (doc.key === DocType.DEFENSE_PPT) {
            navigate('/ppt');
            return;
        }
        // 如果是全文降重功能，需要在ultra模式下
        if (doc.key === DocType.THESIS_MODIFICATION && !summaryStore.useUltra) {
            summaryStore.reset();
            summaryStore.setDocType(doc.key);
            return;
        }
        selectDocType(doc.key);
    };

    // 需求表单里改了写作类型：首页 Tab 跟着切过去（表单不重建）
    const handleWritingTypeChange = (queryType) => {
        const docKey = QUERY_TYPE_TO_DOC_TYPE[queryType];
        if (docKey && docKey !== summaryStore.docType) {
            selectDocType(docKey);
        }
    };

    // 文档类型切换：原首页的样式（白色小卡片 + 彩色图标）；手机上只保留 毕业长文 / 开题报告 / 文献综述
    // 三个（当前选中的不隐藏），排成一行三等分、不滚动的分段选择器（样式见 .docTypeContainer 的手机媒体查询）
    const renderDocTypeTabs = () => (
        <div className={'w-full'}>
            <div className={styles.docTypeContainer}>
                {DOC_TYPES.map((doc) => (
                    <DocTypeItem
                        key={doc.key}
                        doc={doc}
                        isActive={summaryStore.docType === doc.key}
                        isSpecial={doc.key === DocType.THESIS_MODIFICATION}
                        mobileHidden={
                            MOBILE_HIDDEN_DOC_TYPES.includes(doc.key) &&
                            summaryStore.docType !== doc.key
                        }
                        onClick={() => handleDocTypeClick(doc)}
                    />
                ))}
            </div>
        </div>
    );

    // 新版写作首页的标题区：与原首页一致（大标题 + 副标题 + 模型切换）；
    // 手机上不放顶部的模型切换条（表单里的「模型」一栏就是同一个状态），标题区也收紧一些
    const renderWritingHero = () => (
        <div className="lg-container relative z-10 mx-auto">
            <div className={'mt-6 flex w-full flex-col items-center md:mt-16'}>
                <div className="relative">
                    <div className="douyin-meihao relative z-20 text-[28px] leading-tight text-[#192038] md:text-5xl md:leading-normal">
                        灵感风暴 · {curActiveDocItem?.label}
                    </div>
                    <img
                        className="md-w-[170px] absolute bottom-1 right-1 md:h-[25px]"
                        src={titleExtra}
                        alt=""
                    />
                </div>
                <div className="mb-4 mt-2 px-3 text-center text-sm text-primary md:mb-7 md:mt-4 md:text-base">
                    {curActiveDocItem?.subTitle}
                </div>
            </div>
            {!isMobile && <EngineSwitch model={writingModel} onChange={setWritingModel} />}
        </div>
    );

    // 首页直接是需求表单；进入参考资料步后隐藏首页标题 / Tab，同一个 StepForm 不重建，状态保留
    // （各节点的树位置保持不变，只换 className）
    const renderNewWriting = () => (
        <>
            {onWritingHome && renderWritingHero()}
            <div className={styles.documents}>
                <Background useUltra={false} />
                <div
                    className={
                        onWritingHome
                            ? `${styles.content} lg-container`
                            : 'relative z-10 mx-auto w-[calc(100vw-24px)] pb-10 pt-4 md:w-[calc(100vw-48px)] md:pt-8'
                    }
                >
                    {/* 首页：原首页的分步表单容器（带阴影外框 + 类型切换 + 白色表单区） */}
                    <div className={onWritingHome ? styles.stepFormWrapper : 'w-full'}>
                        {onWritingHome && renderDocTypeTabs()}
                        <div className={onWritingHome ? styles.stepFormContainer : 'w-full'}>
                            <StepForm
                                embedded
                                serviceType={DOC_TYPE_TO_QUERY_TYPE[summaryStore.docType]}
                                onServiceTypeChange={handleWritingTypeChange}
                                model={writingModel}
                                onModelChange={setWritingModel}
                                onStepChange={setWritingStep}
                            />
                        </div>
                    </div>
                    {/* 我的生成记录：本机下过单 / 付过款的订单，紧跟在写作表单下方 */}
                    {onWritingHome && <RecentOrders />}
                    {/* 优秀案例库：表单下方，范文点开进入 /case/:id */}
                    {onWritingHome && <CaseLibrary />}
                    {onWritingHome && <DescCards />}
                </div>
                {onWritingHome && (
                    <div className={'z-2 relative'}>
                        <Footer />
                    </div>
                )}
            </div>
        </>
    );

    if (isNewWriting) {
        return (
            <>
                {notiContextHolder}
                {contextHolder}
                {renderNewWriting()}
            </>
        );
    }

    return (
        <>
            {notiContextHolder}
            {contextHolder}
            {(summaryStore.currentStep === 1 && !isGraduationThesis) ||
            (summaryStore.currentStep === 3 && isGraduationThesis) ||
            (summaryStore.currentStep === 4 && isGraduationThesis) ||
            (reduceStore.curStep === 1 && isReduce) ||
            (reduceStore.curStep === 2 && isReduce) ? null : (
                <>
                    {/*分步1*/}
                    {((!isGraduationThesis && summaryStore.currentStep === 0) ||
                        isGraduationThesis) && (
                        <div className="lg-container relative z-10 mx-auto">
                            {/* 当前文档类型对应的titleIcon和subTitle */}
                            <div className={'mt-8 flex w-full flex-col items-center md:mt-16'}>
                                {/* <img
                                    className="h-[38px] md:h-[58px]"
                                    src={curActiveDocItem.titleIcon}
                                    alt=""
                                /> */}
                                <div className="relative">
                                    <div className="douyin-meihao relative z-20 text-[30px] leading-tight text-[#192038] md:text-5xl md:leading-normal">
                                        灵感风暴 · {curActiveDocItem.label}
                                    </div>
                                    <img
                                        className="md-w-[170px] absolute bottom-1 right-1 md:h-[25px]"
                                        src={titleExtra}
                                        alt=""
                                    />
                                </div>
                                <div className="mb-5 mt-3 px-3 text-center text-sm text-primary md:mb-7 md:mt-4 md:text-base">
                                    {curActiveDocItem.subTitle}
                                </div>
                            </div>
                            {/* 切换模式(Normal/Ultra) */}
                            {!summaryStore.isLoadingSummary && <UltraSwitch />}
                        </div>
                    )}
                    {((!isGraduationThesis && summaryStore.currentStep === 0) ||
                        isGraduationThesis) && (
                        <div className={styles.documents}>
                            {/* 背景图 */}
                            <Background useUltra={summaryStore.useUltra} />
                            {/* 内容 */}
                            <div className={`${styles.content} lg-container`}>
                                {/*分步表单*/}
                                <div className={styles.stepFormWrapper}>
                                    {!summaryStore.isLoadingSummary && (
                                        <>
                                            {/* 生成文献类型内容分类 */}
                                            {renderDocTypeTabs()}
                                            {/* 步骤 */}
                                            <div className={`${styles.stepFormContainer}`}>
                                                {/* 步骤Steps */}
                                                <div
                                                    className={twMerge(
                                                        'mb-2 w-full md:mb-8 md:px-20',
                                                        styles.stepsBar,
                                                    )}
                                                >
                                                    <Steps
                                                        current={summaryStore.currentStep}
                                                        onChange={(e) => {
                                                            summaryStore.setCurrentStep(e);
                                                        }}
                                                        labelPlacement={
                                                            isMobile ? 'vertical' : 'horizontal'
                                                        }
                                                        responsive={false}
                                                        items={items}
                                                    />
                                                </div>
                                                {/* 长文、开题、文献综述step1 */}
                                                {isGraduationThesis &&
                                                    summaryStore.currentStep === 0 && (
                                                        <GraduationThesisStep1 />
                                                    )}
                                                {/* PPT step1 */}
                                                {isPPT && <PPTStep1 />}
                                                {/* 降重 step1 */}
                                                {isReduce && <ReduceStep1 />}
                                                {/* 其他 step */}
                                                {!isGraduationThesis && !isPPT && !isReduce && (
                                                    <Step1
                                                        generateOutline={() => {
                                                            summaryStore.getSummary(notiApi);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}

                                    {/* 长文标题 */}
                                    {isGraduationThesis && summaryStore.currentStep === 1 && (
                                        <GraduationThesisStep2 />
                                    )}
                                    {/* 长文文献*/}
                                    {isGraduationThesis &&
                                        summaryStore.currentStep === 2 &&
                                        !summaryStore.isLoadingSummary && (
                                            <GraduationThesisStep3 notiApi={notiApi} />
                                        )}

                                    {/* （公用）大纲加载态*/}
                                    {summaryStore.isLoadingSummary ? (
                                        <>
                                            <Step1Progress />
                                            <div
                                                className={
                                                    'mt-6 rounded-xl border border-[#EEEEF1] px-3 py-4 md:px-28'
                                                }
                                            >
                                                <div className={'mb-6 text-sm'}>
                                                    大纲小节支持{' '}
                                                    <span className={'text-primary'}>增加、</span>
                                                    <span className={'text-primary'}>删除、</span>
                                                    <span className={'text-primary'}>修改</span>
                                                    ；点击图标可插入相应内容
                                                </div>
                                                <div
                                                    className={'flex justify-between px-0 md:px-7'}
                                                >
                                                    <div
                                                        className={
                                                            'flex flex-col items-center justify-between'
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                'flex h-8 w-8 items-center justify-center rounded-full bg-[#A570FF] bg-opacity-10'
                                                            }
                                                        >
                                                            <img
                                                                className={'h-4 w-4'}
                                                                src={iconTableActive}
                                                                alt=""
                                                            />
                                                        </div>
                                                        <div className={'mt-2'}>插入数据表</div>
                                                    </div>
                                                    <div
                                                        className={
                                                            'flex flex-col items-center justify-between'
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                'flex h-8 w-8 items-center justify-center rounded-full bg-[#A570FF] bg-opacity-10'
                                                            }
                                                        >
                                                            <img
                                                                className={'h-4 w-4'}
                                                                src={iconDiagramActive}
                                                                alt=""
                                                            />
                                                        </div>
                                                        <div className={'mt-2'}>插入数据图</div>
                                                    </div>
                                                    <div
                                                        className={
                                                            'flex flex-col items-center justify-between'
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                'flex h-8 w-8 items-center justify-center rounded-full bg-[#A570FF] bg-opacity-10'
                                                            }
                                                        >
                                                            <img
                                                                className={'h-4 w-4'}
                                                                src={iconFormulaActive}
                                                                alt=""
                                                            />
                                                        </div>
                                                        <div className={'mt-2'}>插入公式</div>
                                                    </div>
                                                    <div
                                                        className={
                                                            'flex flex-col items-center justify-between'
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                'flex h-8 w-8 items-center justify-center rounded-full bg-[#A570FF] bg-opacity-10'
                                                            }
                                                        >
                                                            <img
                                                                className={'h-4 w-4'}
                                                                src={iconCodeActive}
                                                                alt=""
                                                            />
                                                        </div>
                                                        <div className={'mt-2'}>插入代码</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                                {/* 我的生成记录（降AIGC 等旧流程 Tab 下同样显示在表单下方） */}
                                <RecentOrders />
                                <DescCards />
                            </div>
                            <div className={'z-2 relative'}>
                                <Footer />
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* 长文、PPT、降重、其他的其他步骤 */}
            <>
                {/*分步2*/}
                {(summaryStore.currentStep === 1 && !isGraduationThesis && !isReduce) ||
                (summaryStore.currentStep === 3 && isGraduationThesis && !isReduce) ? (
                    <div className={styles.documents}>
                        <div
                            className={classNames(styles.bg, {
                                [styles.bgUltra]: summaryStore.useUltra,
                            })}
                        ></div>
                        <div
                            className={`lg-container relative mx-auto my-16 flex flex-col items-center rounded-xl bg-white p-2 pb-4 md:p-10`}
                        >
                            {isMobile ||
                            summaryStore.isLoadingPreview ||
                            summaryStore.isLoadingSummary ? null : (
                                <div
                                    className={`${styles.summaryTips} w-64`}
                                    style={{ top: top + 'px' }}
                                >
                                    <div className={`${styles.bold} mb-3 text-primary`}>TIPS：</div>
                                    <div className={'mb-2'}>👈点亮右侧按钮，可插入相应内容</div>
                                    <div className={styles.line}>
                                        {TableIcon}

                                        <div className={'ml-1.5'}>
                                            插入<span>数据表</span>
                                        </div>
                                    </div>
                                    <div className={styles.line}>
                                        {PicIcon}
                                        <div className={'ml-1.5'}>
                                            插入<span>流程图</span>
                                        </div>
                                    </div>
                                    <div className={styles.line}>
                                        {DataIcon}
                                        <div className={'ml-1.5'}>
                                            插入<span>数据</span>
                                        </div>
                                    </div>
                                    <div className={styles.line}>
                                        {GongshiIcon}
                                        <div className={'ml-1.5'}>
                                            插入<span>公式</span>
                                        </div>
                                    </div>

                                    <div className={styles.line}>
                                        {CodeIcon}
                                        <div className={'ml-1.5'}>
                                            插入<span>代码段</span>
                                        </div>
                                    </div>
                                    <div className={'my-3 border-b border-[#F7F1FF]'}></div>
                                    <div className={styles.line}>
                                        <div>
                                            可根据需要进行 <span>新增、编辑、删除</span> 等操作
                                        </div>
                                    </div>
                                    {summaryStore._tempAutoText && (
                                        <div style={{ lineHeight: '20px' }}>
                                            <span>
                                                灵感会自动根据您的需要点亮相应的
                                                {summaryStore._tempAutoText}
                                            </span>
                                            <br />
                                            您可根据需要进行<span>添加或删减</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div
                                className={
                                    'w-full origin-left scale-75 md:mb-8 md:scale-100 md:px-20'
                                }
                            >
                                <Steps
                                    current={summaryStore.currentStep}
                                    labelPlacement={isMobile ? 'vertical' : 'horizontal'}
                                    responsive={false}
                                    onChange={(e) => {
                                        summaryStore.setCurrentStep(e);
                                    }}
                                    items={items}
                                />
                            </div>
                            {(!isGraduationThesis &&
                                summaryStore.isLoadingSummary &&
                                summaryStore.currentStep === 1) ||
                            (isGraduationThesis &&
                                summaryStore.isLoadingSummary &&
                                summaryStore.currentStep === 3) ? (
                                <Step1Progress />
                            ) : null}

                            {(!isGraduationThesis &&
                                summaryStore.isLoadingPreview &&
                                summaryStore.currentStep === 1) ||
                            (isGraduationThesis &&
                                summaryStore.isLoadingPreview &&
                                summaryStore.currentStep === 3) ? (
                                <Progress
                                    loading={summaryStore.isLoadingPreview}
                                    end={!summaryStore.isLoadingPreview}
                                    desc="灵感正在帮您生成预览，预计需要等待60s左右，请耐心等待~"
                                    list={['启动中...', '检索文献', '分析大纲', '生成预览']}
                                    distance={600}
                                />
                            ) : null}
                            {(!isGraduationThesis &&
                                summaryStore.currentStep === 1 &&
                                summaryStore.paragraphs &&
                                summaryStore.paragraphs.length > 0) ||
                            (isGraduationThesis &&
                                summaryStore.currentStep === 3 &&
                                summaryStore.paragraphs &&
                                summaryStore.paragraphs.length > 0) ? (
                                <Step2 />
                            ) : null}
                        </div>
                    </div>
                ) : null}
                {/*分步3*/}
                {(summaryStore.currentStep === 2 && !isGraduationThesis && !isReduce) ||
                (summaryStore.currentStep === 4 && isGraduationThesis && !isReduce) ? (
                    <Step3 />
                ) : null}
            </>
            {/* ---------- */}
            {/* 切换ultra模式弹窗 */}
            <UltraDialog />
            {/* 引用文献提示弹窗 */}
            <ReferenceDocumentModal
                open={summaryStore.confirmCustomReferenceDocumentModal}
                onOk={() => {
                    summaryStore.isConfirmCustomReferenceDocument = true;
                    summaryStore.confirmCustomReferenceDocumentModal = false;
                }}
                onCancel={() => {
                    summaryStore.confirmCustomReferenceDocumentModal = false;
                }}
            />
            {/* 内容丰富提示弹窗 */}
            <ContentEnrichmentModal
                open={summaryStore.showCheckTableDialog}
                onConfirm={() => {
                    summaryStore.onModalConfirmGetSummary(notiApi);
                }}
                autoLightTypes={summaryStore.autoLightTypes}
                onAutoLightChange={(e) => summaryStore.onAutoLightChange(e)}
                isLoadingSummary={summaryStore.isLoadingSummary}
            />
        </>
    );
});
